import { useEffect, useRef, useState } from 'react';
import { apiDelete, apiGet, apiPost } from '@shared/api/httpClient';
import { formatLocalDateTime } from '@shared/utils/dateTime';

type ExportJob = {
  jobId: string;
  status: 'QUEUED' | 'RUNNING' | 'FINALIZING' | 'COMPLETED' | 'CANCELLED' | 'FAILED';
  createdAt: string;
  expiresAt: number;
  processedInvitations: number;
  eventCount: number;
  message?: string;
};
const FILTERS = ['status', 'accessed', 'company', 'recipient', 'email', 'campaign', 'search',
  'createdFrom', 'createdTo', 'lastAccessFrom', 'lastAccessTo'];
const active = (job: ExportJob) => ['QUEUED', 'RUNNING', 'FINALIZING'].includes(job.status);

export default function OutreachExport({ filters, disabled }: {
  filters: Record<string, string | number>; disabled: boolean;
}) {
  const [jobs, setJobs] = useState<ExportJob[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [loaded, setLoaded] = useState(false);
  // Retain the request ID after a network failure so retrying cannot start a duplicate job.
  const request = useRef<{ id: string; serialized: string } | null>(null);
  const latest = useRef<ExportJob[]>([]);

  useEffect(() => {
    let disposed = false;
    let timer: ReturnType<typeof setTimeout>;
    async function poll() {
      try {
        const response = await apiGet<{ items: ExportJob[] }>('/admin/outreach/exports');
        // The history index is eventually consistent; refresh known active jobs directly.
        const recent = [...latest.current, ...response.items].filter(job => job.expiresAt * 1000 > Date.now());
        const ids = [...new Set(recent.filter(active).map(job => job.jobId))];
        const updated = await Promise.all(ids.map(id => apiGet<ExportJob>(`/admin/outreach/exports/${id}`)));
        if (disposed) return;
        const map = new Map(recent.map(job => [job.jobId, job]));
        for (const job of updated) map.set(job.jobId, job);
        const next = [...map.values()].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, 10);
        latest.current = next;
        setJobs(next);
        setLoaded(true);
        setError(null);
      } catch (err) {
        if (!disposed) setError(err instanceof Error ? err.message : 'Unable to check export progress. Retrying automatically.');
      } finally {
        if (!disposed) timer = setTimeout(() => void poll(), 5000);
      }
    }
    void poll();
    return () => { disposed = true; clearTimeout(timer); };
  }, [refresh]);

  async function start() {
    setBusy(true);
    setError(null);
    const selected = Object.fromEntries(FILTERS.map(key => [key, String(filters[key] ?? '')]));
    const serialized = JSON.stringify(selected);
    if (request.current?.serialized !== serialized) request.current = { id: crypto.randomUUID(), serialized };
    try {
      const job = await apiPost<ExportJob>('/admin/outreach/exports', { requestId: request.current.id, filters: selected });
      request.current = null;
      latest.current = [job, ...latest.current.filter(item => item.jobId !== job.jobId)];
      setJobs(latest.current);
      setRefresh(value => value + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to start export. Retry to check the same request.');
    } finally { setBusy(false); }
  }

  async function cancel(job: ExportJob) {
    setBusy(true);
    try {
      const updated = await apiDelete<ExportJob>(`/admin/outreach/exports/${job.jobId}`);
      latest.current = latest.current.map(item => item.jobId === job.jobId ? updated : item);
      setJobs(latest.current);
      setRefresh(value => value + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to cancel export.');
    } finally { setBusy(false); }
  }

  async function download(job: ExportJob) {
    setBusy(true);
    try {
      const { downloadUrl } = await apiGet<{ downloadUrl: string }>(`/admin/outreach/exports/${job.jobId}/download`);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.rel = 'noopener noreferrer';
      link.referrerPolicy = 'no-referrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to download export.');
    } finally { setBusy(false); }
  }

  return <article className="card">
    <div className="grid-header-row">
      <div>
        <h3>Event CSV exports</h3>
        <p className="muted-text">Export invitations matching the applied search filters. Processing continues if you close this page. Return here to download your completed exports within 24 hours.</p>
        <p className="muted-text">Events are bounded by the export start time. Invitation edits during processing may affect results. Older opens without retained events remain explicitly marked.</p>
      </div>
      <button className="secondary-button" type="button" disabled={disabled || busy || !loaded || jobs.some(active)} onClick={() => void start()}>
        {busy ? 'Please wait...' : 'Prepare event CSV'}
      </button>
    </div>
    {error && <p role="alert">{error}</p>}
    <div aria-live="polite">
      {jobs.map(job => <div key={job.jobId} className="grid-header-row">
        <div>
          <strong>{formatLocalDateTime(job.createdAt)} · {job.status}</strong>
          <p>{job.processedInvitations.toLocaleString()} invitations processed · {job.eventCount.toLocaleString()} events collected</p>
          {job.message && <p>{job.message}</p>}
        </div>
        {active(job) && <button className="secondary-button" disabled={busy} onClick={() => void cancel(job)}>Cancel export</button>}
        {job.status === 'COMPLETED' && <button className="secondary-button" disabled={busy} onClick={() => void download(job)}>Download event CSV</button>}
      </div>)}
    </div>
  </article>;
}
